import { first } from "lodash";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import apiService from "../../../../api/rest";
import { SENTRY_ERRORS } from "../../../../app-constants";

export const getDefaultState = () => {
  return {
    levelsList: null,
    activeLevelID: null,
    activeSectionID: null,
    activeRowID: null,
    sectionsList: null,
    activeSeatID: null
  };
};

// initial state
const state = getDefaultState();

const isActiveSeatSelectionValid = async (rootGetters, venueLevels) => {
  //check if the selected level is on new data
  const activeLevelID = rootGetters["level/getActiveLevelId"];
  if (venueLevels.filter((level) => activeLevelID === level.id).length === 0) return false;

  const venueId = rootGetters["venue/getActiveVenue"];
  const payload = { venueId, levelId: activeLevelID };

  //get seat for that level and compare with the active selection
  return apiService
    .fetchLevelSeats(payload)
    .then((response) => {
      const sectionList = response.data.sections;
      const activeSectionId = rootGetters["level/getActiveSectionId"];

      const section = sectionList.find((section) => section.id === activeSectionId);
      //return if not section match with the activeSection
      if (section.length === 0) return false;

      const activeRowId = rootGetters["level/getActiveRowId"];
      const row = section.rows.find((row) => row.id === activeRowId);
      //return if a row match with active selection
      return row && true;
    })
    .catch((err) => {
      return false;
    });
};

// getters
const getters = {
  getLevelsList(state) {
    return state.levelsList;
  },
  getActiveLevelId(state) {
    return state.activeLevelID;
  },
  getActiveSectionId(state) {
    return state.activeSectionID;
  },
  getActiveRowId(state) {
    return state.activeRowID;
  },
  getActiveSeatId(state) {
    return state.activeSeatID;
  },
  getSectionsList(state) {
    return state.sectionsList || [];
  },
  getRowsList(state) {
    const section = (state.sectionsList || []).find((section) => section.id === state.activeSectionID);
    return section?.rows || [];
  },
  getSeatsList(state, getters) {
    const rowsList = (getters.getRowsList || []).find((row) => row.id === state.activeRowID);
    return rowsList?.seats || [];
  },
  getCurrentSelection(state, getters, rootState, rootGetters) {
    return {
      venueId: rootGetters["venue/getActiveVenue"],
      levelId: state.activeLevelID,
      sectionId: state.activeSectionID,
      rowId: state.activeRowID,
      seatId: state.activeSeatID
    };
  },
  isValid(state) {
    return !!(state.activeLevelID && state.activeSectionID && state.activeRowID);
  }
};

// actions
const actions = {
  async fetchVenueLevels({ commit, rootGetters }, venueId) {
    return apiService.fetchVenueLevels(venueId).then(async (response) => {
      const venueLevels = response.data && response.data.levels;
      //if seat is selected, confirm that is still available after get levels
      const isSeatValid = await isActiveSeatSelectionValid(rootGetters, venueLevels);
      if (!isSeatValid) {
        commit("delivery/resetSeatSelection", null, { root: true });
        commit("vendor/setVendorsList", null, { root: true });
      }
      commit("setLevelsList", venueLevels);
      return venueLevels;
    });
  },
  async fetchLevelSeats({ state, commit, getters }, payload) {
    if (state.activeLevelID === +payload.levelId) {
      return;
    }

    try {
      const response = await apiService.fetchLevelSeats(payload);
      commit("setActiveLevelID", +payload.levelId);
      commit("setSectionsList", response.data.sections);
      commit("setActiveSectionID", first(getters.getSectionsList)?.id || null);
      commit("setActiveRowID", first(getters.getRowsList)?.id || null);
      commit("setActiveSeatID", first(getters.getSeatsList)?.id || null);

      return response;
    } catch (error) {
      errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_FETCH_VENDORS, "fetchLevelSeats", "fetchLevelSeats", {}, error);
      throw error;
    }
  },
  switchSection({ getters, commit }, sectionId) {
    commit("setActiveSectionID", +sectionId);
    commit("setActiveRowID", first(getters.getRowsList)?.id || null);
    commit("setActiveSeatID", first(getters.getSeatsList)?.id || null);
  },
  switchRow({ commit, getters }, rowId) {
    commit("setActiveRowID", +rowId);
    commit("setActiveSeatID", first(getters.getSeatsList)?.id || null);
  },
  fetchLocationVendors({ getters, dispatch }) {
    return dispatch("vendor/fetchLocationVendors", getters.getCurrentSelection, {
      root: true
    });
  },
  switchSeat({ commit }, seatId) {
    commit("setActiveSeatID", +seatId);
  },
  setActiveSeatSelection({ getters, commit }, seatSelection) {
    commit("setActiveLevelID", +seatSelection.levelId);
    commit("setActiveSectionID", +seatSelection.sectionId);

    //Both rowID and seatID need to be autoselected if rowId is not valid
    if (!seatSelection.rowId) {
      const rowId = getters.getRowsList[0]?.id || null;
      commit("setActiveRowID", rowId);

      const seatId = getters.getSeatsList[0]?.id || null;
      commit("setActiveSeatID", seatId);
      return;
    }
    commit("setActiveRowID", +seatSelection.rowId);

    if (!seatSelection.seatId) {
      const seatId = getters.getSeatsList[0]?.id || null;
      commit("setActiveSeatID", seatId);
      return;
    }
    commit("setActiveSeatID", +seatSelection.seatId);
  }
};

// mutations
const mutations = {
  setLevelsList(state, collection) {
    state.levelsList = collection;
  },
  setActiveLevelID(state, id) {
    state.activeLevelID = id;
  },
  setActiveSectionID(state, id) {
    state.activeSectionID = id;
  },
  setActiveRowID(state, id) {
    state.activeRowID = id;
  },
  setSectionsList(state, collection) {
    state.sectionsList = collection;
  },
  setActiveSeatID(state, id) {
    state.activeSeatID = id;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },

  resetActiveLevelSelection(state) {
    state.activeLevelID = null;
    state.activeSectionID = null;
    state.activeRowID = null;
    state.activeSeatID = null;
  },

  resetLevelLists(state) {
    state.levelsList = null;
    state.sectionsList = null;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
